import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TaskDepartment, TaskPersonOption, TasksService } from '../../services/tasks.service';
import { TaskAvatarComponent } from '../task-avatar/task-avatar.component';

export type PickTarget = { kind: 'person'; person: TaskPersonOption } | { kind: 'department'; department: TaskDepartment; everyone?: boolean } | { kind: 'me' };

/**
 * Searchable list of people (and, when allowed, department queues).
 * Opens as a popover under its trigger — or above it when the window has no room below, as for
 * "Waiting on…" at the foot of the task drawer (2 Nov 2026); the trigger is projected content.
 */
@Component({
  selector: 'tk-people-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, TaskAvatarComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './people-picker.component.html',
  styleUrls: ['./people-picker.component.scss'],
})
export class PeoplePickerComponent implements OnInit {
  private readonly tasks = inject(TasksService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly host = inject(ElementRef<HTMLElement>);

  /** Show department queues as options. */
  @Input() departments = false;
  /** Also offer "Everyone in <department>": one task per member. */
  @Input() everyone = false;
  /** Ids already chosen (shown ticked, still clickable to remove). */
  @Input() chosen: string[] = [];
  /** Offer "Me" at the top. */
  @Input() allowMe = true;
  /** Hide these user ids (e.g. yourself when choosing who you wait on). */
  @Input() exclude: string[] = [];
  @Input() placeholder = 'Search people…';
  @Input() align: 'left' | 'right' = 'left';
  @Output() picked = new EventEmitter<PickTarget>();
  @Output() closed = new EventEmitter<void>();

  @ViewChild('search') searchEl?: ElementRef<HTMLInputElement>;

  open = false;
  /** Opens above the trigger (too little room below it). */
  up = false;
  /** The list's height, so the whole popover stays inside the window. */
  listMax = 320;
  query = '';
  people: TaskPersonOption[] = [];
  depts: TaskDepartment[] = [];
  loading = false;
  cursor = 0;
  myId = localStorage.getItem('guestID') || '';

  ngOnInit() {
    void this.load();
  }

  private async load() {
    this.loading = true;
    try {
      [this.people, this.depts] = await Promise.all([this.tasks.people(), this.departments ? this.tasks.departments() : Promise.resolve([])]);
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  toggle(ev?: Event) {
    ev?.stopPropagation();
    this.open ? this.close() : this.show();
  }

  show() {
    const r = this.host.nativeElement.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - 16;
    const above = r.top - 16;
    this.up = below < 260 && above > below;
    // The search row and paddings take about 64px; the list gets the rest, 140–320px.
    this.listMax = Math.max(140, Math.min(320, (this.up ? above : below) - 64));
    this.open = true;
    this.query = '';
    this.cursor = 0;
    this.cdr.markForCheck();
    setTimeout(() => this.searchEl?.nativeElement.focus(), 0);
  }

  close() {
    if (!this.open) return;
    this.open = false;
    this.closed.emit();
    this.cdr.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  onDocClick(ev: MouseEvent) {
    if (this.open && !this.host.nativeElement.contains(ev.target as Node)) this.close();
  }

  @HostListener('document:keydown.escape')
  onEsc() {
    this.close();
  }

  get rows(): PickTarget[] {
    const q = this.query.trim().toLowerCase();
    const ex = new Set(this.exclude);
    const out: PickTarget[] = [];
    if (this.allowMe && !q && !ex.has(this.myId)) out.push({ kind: 'me' });
    if (this.departments) {
      for (const d of this.depts) {
        if (!q || d.name.toLowerCase().includes(q)) {
          out.push({ kind: 'department', department: d });
          if (this.everyone && d.members > 0) out.push({ kind: 'department', department: d, everyone: true });
        }
      }
    }
    for (const p of this.people) {
      if (ex.has(p.userId)) continue;
      if (q && !`${p.name} ${p.userName} ${p.department}`.toLowerCase().includes(q)) continue;
      out.push({ kind: 'person', person: p });
    }
    return out.slice(0, 60);
  }

  /** Departments first only when searching; otherwise people first, departments after a divider. */
  get grouped(): { label: string; rows: PickTarget[] }[] {
    const rows = this.rows;
    const groups: { label: string; rows: PickTarget[] }[] = [];
    const me = rows.filter((r) => r.kind === 'me');
    const people = rows.filter((r) => r.kind === 'person');
    const depts = rows.filter((r) => r.kind === 'department');
    if (me.length) groups.push({ label: '', rows: me });
    // Typing a department's name puts the queue first; otherwise people come first.
    const q = this.query.trim().toLowerCase();
    const deptFirst = !!q && depts.some((d) => d.kind === 'department' && d.department.name.toLowerCase().startsWith(q));
    const peopleGroup = { label: 'People', rows: people };
    const deptGroup = { label: 'Department queues', rows: depts };
    for (const g of deptFirst ? [deptGroup, peopleGroup] : [peopleGroup, deptGroup]) if (g.rows.length) groups.push(g);
    return groups;
  }

  flat(): PickTarget[] {
    return this.grouped.flatMap((g) => g.rows);
  }
  /** Department names as a quick strip at the top while nothing is typed. */
  get deptStrip(): TaskDepartment[] {
    return this.departments && !this.query.trim() ? this.depts : [];
  }
  jumpTo(d: TaskDepartment) {
    this.query = d.name;
    this.cursor = 0;
    this.cdr.markForCheck();
  }
  isChosen(r: PickTarget): boolean {
    return r.kind === 'person' ? this.chosen.includes(r.person.userId) : r.kind === 'department' ? this.chosen.includes(`${r.everyone ? 'all:' : 'queue:'}${r.department.name}`) : false;
  }

  onKey(ev: KeyboardEvent) {
    const list = this.flat();
    if (ev.key === 'ArrowDown') {
      this.cursor = Math.min(this.cursor + 1, list.length - 1);
      ev.preventDefault();
    } else if (ev.key === 'ArrowUp') {
      this.cursor = Math.max(this.cursor - 1, 0);
      ev.preventDefault();
    } else if (ev.key === 'Enter') {
      if (list[this.cursor]) this.pick(list[this.cursor]);
      ev.preventDefault();
    } else {
      this.cursor = 0;
    }
  }

  indexOf(row: PickTarget): number {
    return this.flat().indexOf(row);
  }

  pick(row: PickTarget) {
    this.picked.emit(row);
    this.close();
  }

  trackRow = (_: number, r: PickTarget) => (r.kind === 'person' ? r.person.userId : r.kind === 'department' ? `${r.everyone ? 'all:' : 'queue:'}${r.department.name}` : 'me');
}
